import { useQuery } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import {
  linkSchema,
  richTextSchema,
  sermonVideoProvider,
  type RichText,
  type SermonVideoProvider,
} from "@how/website-core";

/*
 * The member's half of sermons and media (ADR 0061, ADR 0037): the church's published sermons, to browse, watch,
 * listen to and follow the notes of. The policies admit published sermons only, to a member with portal.access in a
 * church with sermons_media; nothing here writes.
 */

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

export interface PortalSermon {
  readonly id: string;
  readonly title: string;
  readonly slug: string;
  readonly preachedOn: string;
  readonly summary: string | null;
  readonly scripture: readonly string[];
  readonly speaker: { readonly name: string; readonly role: string | null } | null;
  readonly series: { readonly id: string; readonly title: string } | null;
  readonly video: { readonly provider: SermonVideoProvider; readonly url: string } | null;
  /** An uploaded file's path in how-audio, or the church's own link. */
  readonly audio:
    | { readonly path: string; readonly durationSeconds: number | null }
    | { readonly url: string; readonly durationSeconds: number | null }
    | null;
  readonly notes: RichText | null;
  readonly resources: readonly { readonly label: string; readonly href: string }[];
}

export function usePortalSermons() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["portal-sermons", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<PortalSermon[]> => {
      const [sermons, series, speakers] = await Promise.all([
        supabase
          .from("sermons")
          .select(
            "id, title, slug, preached_on, summary, scripture, speaker_id, series_id, video_url, audio_path, audio_url, audio_duration_seconds, notes, resources",
          )
          // RLS admits every church the reader belongs to, and only published sermons; the screen shows one church.
          .eq("tenant_id", tenantId ?? "")
          .eq("is_published", true)
          .order("preached_on", { ascending: false })
          .limit(500),
        supabase
          .from("sermon_series")
          .select("id, title")
          .eq("tenant_id", tenantId ?? ""),
        supabase
          .from("sermon_speakers")
          .select("id, name, role_title")
          .eq("tenant_id", tenantId ?? ""),
      ]);
      if (sermons.error) throw sermons.error;
      if (series.error) throw series.error;
      if (speakers.error) throw speakers.error;
      const seriesById = new Map(series.data.map((s) => [s.id, s]));
      const speakerById = new Map(speakers.data.map((s) => [s.id, s]));
      return sermons.data.map((row) => {
        const notes = richTextSchema.safeParse(row.notes);
        const resources = linkSchema.array().safeParse(row.resources);
        const provider = row.video_url === null ? null : sermonVideoProvider(row.video_url);
        const speaker = row.speaker_id === null ? undefined : speakerById.get(row.speaker_id);
        const inSeries = row.series_id === null ? undefined : seriesById.get(row.series_id);
        return {
          id: row.id,
          title: row.title,
          slug: row.slug,
          preachedOn: row.preached_on,
          summary: row.summary,
          scripture: row.scripture,
          speaker: speaker === undefined ? null : { name: speaker.name, role: speaker.role_title },
          series: inSeries === undefined ? null : { id: inSeries.id, title: inSeries.title },
          video:
            provider === null || row.video_url === null ? null : { provider, url: row.video_url },
          audio:
            row.audio_path !== null
              ? { path: row.audio_path, durationSeconds: row.audio_duration_seconds }
              : row.audio_url !== null
                ? { url: row.audio_url, durationSeconds: row.audio_duration_seconds }
                : null,
          notes: notes.success ? notes.data : null,
          resources: resources.success ? resources.data : [],
        };
      });
    },
  });
}
